/**
 * ROUND7 - the knife, the crouched walk, the ghillie wrap and the dark (docs/milestones/ROUND7-contracts.md).
 * A file of its own because `tuning.ts` is at the 300-line limit. First guesses, tried against staged
 * approaches in tests/unit/stealth*.test.ts; where one moved, its comment says why.
 */

export const STEALTH = {
  /** How far a stab reaches, centre to centre, metres - a short lunge included. It was 1.35 (`KNIFE.reach`). */
  knifeReach: 1.8,
  /** Degrees either side of the aim a stab still finds its man. It was 40. */
  knifeArcDeg: 60,
  /**
   * Closer than this, metres, he is taken whatever the aim says: a man at arm's length is never a miss. It was
   * 0.95, barely more than two bodies touching (0.75). The measured miss was a ranger stopped 1.2-1.5 m behind
   * a sentry with the cursor on the man's head, which lands on the ground behind the ranger and turns the aim
   * round; at 1.2 the near half of that lands even before the aim fix. It only ever turns a whiff into a hit:
   * a man inside the arc is still preferred (`knifeTarget`).
   */
  knifePointBlank: 1.2,
  /**
   * A stab is stopped by anything solid taller than this between the two men, metres: a wall, a building,
   * sandbags, a crate, the wire. A lunge goes round things, not through them.
   */
  kneeHeight: 0.5,
  /**
   * How close, metres, a man who is NOT looking his way notices the ranger by ear. Replaces the all-round
   * 2.2 m `ENEMY.senseRange`, which spotted a crawler from behind before any knife could reach. A man
   * standing still makes no sound. Walking is heard outside the knife's reach on purpose: the quiet kill
   * is the sneak's or the crawl's, and a walker who wants it has to slow down for the last few metres.
   * Light Step, wading and deep snow move these as they move his footsteps (`hearRange`).
   */
  hear: { run: 4.5, walk: 2.5, sneak: 1.0, crawl: 0.6, still: 0 },
  /** The crouched walk, m/s (walk 2.2, crawl 0.9). */
  sneakSpeed: 1.3,
  /**
   * Footstep noise radius while sneaking, metres: between a crawl's 1.5 and a walk's 6, so the gaits run quietest
   * to loudest crawl, sneak, walk, run. It was 1.0 - quieter than a crawl - until the owner asked about it in play.
   * Still short of what spoils a stab: from 2 m in to the knife's reach is one footstep at a sneak.
   */
  sneakNoise: 2.0,
  /** Detection rate while sneaking: the design's "crouched" (02 §6.2), as walking in a trench already was. */
  sneakDetection: 0.6,
  /** Detection rate crawling (02 §6.2 "prone"; Ghost multiplies it), and walking in a trench, head showing. */
  proneDetection: 0.25,
  trenchDetection: 0.6,
  /**
   * The height a watcher must see to see him, metres (standing 1.5, crawling 0.4). It was 0.85: behind a
   * 0.9 m run of sandbags that hid him only from men more than 5.3 m off - where the 5 m instant spot ends,
   * so the cover barely showed. At 0.8 the bags hide him from anyone beyond 2.8 m and a hedge (1.1) from
   * nearly anywhere, while a man who is standing is seen over both, as before.
   */
  sneakTargetHeight: 0.8,
  /** Ghillie wrap, detection-rate multipliers by posture (02 §7: "prone detection x0.6"). */
  ghillie: { crawl: 0.6, sneak: 0.8, stand: 1 },
  /** Light (02 §6.2): detection rate by the hour. A searchlight on him, or a lightning stroke, counts as day. */
  light: { dawn: 0.8, day: 1, dusk: 0.8, night: 0.55 },
} as const;
